/**
 * Inspector — right sidebar that details the currently-selected
 * node or edge. Edits route through callbacks so the BlueprintBuilder
 * owns the source of truth.
 */

import { useTranslation } from 'react-i18next';
import type { Edge, Node } from '@xyflow/react';

import { Button } from '@/components/Button';
import type { BlueprintMergePolicy } from '@/api/types';

import type { BlueprintEdgeData, BlueprintNodeData } from './types';

const MERGE_POLICIES: BlueprintMergePolicy[] = ['first', 'concat', 'error'];

export function Inspector({
  selectedNode,
  selectedEdge,
  onDeleteNode,
  onDeleteEdge,
  onEdgeChange,
}: {
  selectedNode: Node | undefined;
  selectedEdge: Edge | undefined;
  onDeleteNode: (id: string) => void;
  onDeleteEdge: (id: string) => void;
  onEdgeChange: (id: string, changes: Partial<BlueprintEdgeData>) => void;
}) {
  const { t } = useTranslation('blueprint');

  if (!selectedNode && !selectedEdge) {
    return (
      <div className="h-full rounded-lg border border-dashed border-border bg-surface p-4 text-sm text-ink-muted">
        {t('inspector.select_body')}
      </div>
    );
  }

  if (selectedEdge) {
    const data = (selectedEdge.data ?? {}) as unknown as BlueprintEdgeData;
    return (
      <div className="flex h-full flex-col gap-3 rounded-lg border border-border bg-surface p-4">
        <h3 className="text-sm font-semibold text-ink">{t('inspector.edge_title')}</h3>
        <dl className="space-y-2 text-xs">
          <Row label={t('inspector.source')}>
            <span className="font-mono">
              {selectedEdge.source}.{selectedEdge.sourceHandle ?? '—'}
            </span>
          </Row>
          <Row label={t('inspector.target')}>
            <span className="font-mono">
              {selectedEdge.target}.{selectedEdge.targetHandle ?? '—'}
            </span>
          </Row>
        </dl>

        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-medium text-ink">{t('inspector.merge_policy_label')}</span>
          <select
            value={data.merge_policy ?? 'first'}
            onChange={(e) =>
              onEdgeChange(selectedEdge.id, {
                merge_policy: e.target.value as BlueprintMergePolicy,
              })
            }
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-ink focus-visible:border-spark"
          >
            {MERGE_POLICIES.map((p) => (
              <option key={p} value={p}>
                {t(`inspector.merge.${p}`)}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-ink-subtle">
            {t('inspector.merge_policy_hint')}
          </span>
        </label>

        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-medium text-ink">{t('inspector.retries_label')}</span>
          <input
            type="number"
            min={0}
            max={8}
            value={data.max_retries ?? 0}
            onChange={(e) =>
              onEdgeChange(selectedEdge.id, {
                max_retries: Math.max(0, Math.min(8, parseInt(e.target.value, 10) || 0)),
              })
            }
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-ink focus-visible:border-spark"
          />
          <span className="text-[10px] text-ink-subtle">
            {t('inspector.retries_hint')}
          </span>
        </label>

        <div className="mt-auto pt-2">
          <Button
            variant="danger"
            size="sm"
            onClick={() => onDeleteEdge(selectedEdge.id)}
          >
            {t('inspector.delete_edge')}
          </Button>
        </div>
      </div>
    );
  }

  if (selectedNode) {
    const data = (selectedNode.data ?? {}) as unknown as BlueprintNodeData;
    return (
      <div className="flex h-full flex-col gap-3 rounded-lg border border-border bg-surface p-4">
        <h3 className="text-sm font-semibold text-ink">{t('inspector.node_title')}</h3>
        <dl className="space-y-2 text-xs">
          <Row label="name"><span>{data.name}</span></Row>
          <Row label="version"><span className="font-mono">v{data.version}</span></Row>
          <Row label="inputs">
            <span>{data.inputs.map((p) => p.name).join(', ') || '(none)'}</span>
          </Row>
          <Row label="outputs">
            <span>{data.outputs.map((p) => p.name).join(', ') || '(none)'}</span>
          </Row>
        </dl>
        <div className="mt-auto pt-2">
          <Button
            variant="danger"
            size="sm"
            onClick={() => onDeleteNode(selectedNode.id)}
          >
            {t('inspector.delete_node')}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-20 shrink-0 text-[10px] uppercase tracking-wider text-ink-subtle">{label}</dt>
      <dd className="min-w-0 flex-1 text-ink">{children}</dd>
    </div>
  );
}
